import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { type AxiosResponse } from "axios";
import { awsClientGateway } from "./common/gateway";
import { logMessage } from "../utils/amplifyLogger";
import { clientLog } from "./logging";
import type { RootState } from "../store/types";

type RejectFn = (value: string) => unknown;

const getQueueData = async (
  instanceId: string,
  rejectWithValue: RejectFn,
): Promise<AxiosResponse | unknown> => {
  try {
    const response = await awsClientGateway({
      pathTemplate: `/connectApis/${instanceId}?apiName=queue`,
      method: "GET",
    });
    return response;
  } catch (error) {
    clientLog({
      component: `queue`,
      message: `Something went wrong in getQueueData API => ${error}`,
    });
    logMessage(
      `queue`,
      `Something went wrong in getQueueData API => ${error}`,
      `error`,
    );
    return rejectWithValue("Error in getQueueData api endpoint");
  }
};

export const getQueue = createAsyncThunk<unknown, string>(
  "queue/getQueue",
  async (instanceId: string, { rejectWithValue }) => {
    return await getQueueData(instanceId, rejectWithValue);
  },
);

export const deleteQueue = createAsyncThunk<
  unknown,
  string,
  { state: RootState }
>("queue/deleteQueue", async (id: string, { getState, rejectWithValue }) => {
  try {
    const connectedInstance = getState().userInstance.connectedInstance;
    const instanceId = connectedInstance?.instanceId ?? "";
    await awsClientGateway({
      pathTemplate: `/connectApis/${instanceId}/${id}?apiName=queue`,
      method: "DELETE",
    });
    return await getQueueData(String(instanceId), rejectWithValue);
  } catch (error) {
    clientLog({
      component: `queue`,
      message: `Something went wrong in deleteQueue API => ${error}`,
    });
    logMessage(
      `queue`,
      `Something went wrong in deleteQueue API => ${error}`,
      `error`,
    );
    const axiosError = error as {
      response?: {
        data?: { error?: { message?: string }; message?: string } | string;
      };
      message?: string;
    };
    const errorMessage =
      (typeof axiosError.response?.data === "object"
        ? (axiosError.response?.data?.error?.message ??
          axiosError.response?.data?.message)
        : axiosError.response?.data) ||
      axiosError.message ||
      "Error in deleteQueue api endpoint";
    return rejectWithValue(errorMessage);
  }
});

export const getResetQueue = createAction("queue/getResetQueue");
